import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    ClickAwayListener,
    IconButton,
    InputAdornment,
    Paper,
    Popper,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CloseIcon from '@mui/icons-material/Close';
import { appBarActionButtonSx } from '../../styles/msiAppBarStyles';
import { addDialogFieldSx } from './WatchlistAddSecurityPrimitives';

// Long enough that the table does not redraw on every keystroke.
export const WATCHLIST_QUICK_FILTER_DELAY_MS = 200;

// Works like a browser's find bar: the box stays open while it filters the
// table, and closing it clears the filter, so a filtered table is never
// mistaken for the whole Watchlist.
export default function WatchlistQuickFilter({
    query,
    onQueryChange,
    matchCount,
    totalCount,
    disabled = false,
}) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [open, setOpen] = useState(Boolean(query));
    // Returning to the tab shows an active filter without taking the focus.
    const [focusOnOpen, setFocusOnOpen] = useState(false);
    const [draft, setDraft] = useState(query);
    const inputRef = useRef(null);

    useEffect(() => {
        if (draft === query) return undefined;
        const timer = window.setTimeout(() => onQueryChange(draft), WATCHLIST_QUICK_FILTER_DELAY_MS);
        return () => window.clearTimeout(timer);
    }, [draft, onQueryChange, query]);

    const clear = () => {
        setDraft('');
        onQueryChange('');
    };
    const close = () => {
        clear();
        setOpen(false);
    };
    const toggle = () => {
        if (open) {
            close();
            return;
        }
        setFocusOnOpen(true);
        setOpen(true);
    };
    const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            close();
            anchorEl?.focus();
        } else if (event.key === 'Enter') {
            onQueryChange(draft);
        }
    };
    // An empty box closes on a click elsewhere; one that is filtering stays.
    const handleClickAway = (event) => {
        if (draft || anchorEl?.contains(event.target)) return;
        setOpen(false);
    };

    return (
        <>
            <Tooltip title={open ? 'Close search' : 'Search watchlist'}>
                <span>
                    <IconButton
                        ref={setAnchorEl}
                        size="small"
                        onClick={toggle}
                        disabled={disabled}
                        aria-label={open ? 'Close watchlist search' : 'Search watchlist'}
                        aria-expanded={open}
                        sx={[
                            appBarActionButtonSx,
                            open && { color: 'primary.main', borderColor: 'primary.main' },
                        ]}
                    >
                        <SearchRoundedIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
            <Popper
                open={open && Boolean(anchorEl)}
                anchorEl={anchorEl}
                placement="bottom-end"
                popperOptions={{ strategy: 'fixed' }}
                modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
                sx={{ zIndex: (theme) => theme.zIndex.appBar + 1 }}
            >
                <ClickAwayListener onClickAway={handleClickAway}>
                    <Paper
                        elevation={6}
                        sx={{ p: 1, width: 340, maxWidth: 'calc(100vw - 24px)', borderRadius: 2 }}
                    >
                        <TextField
                            autoFocus={focusOnOpen}
                            fullWidth
                            size="small"
                            value={draft}
                            onChange={(event) => setDraft(event.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Name, ticker, country or attribute"
                            inputRef={inputRef}
                            inputProps={{ 'aria-label': 'Filter watchlist', maxLength: 128 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchRoundedIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {query && (
                                            <Typography
                                                role="status"
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ whiteSpace: 'nowrap', mr: 0.25 }}
                                            >
                                                {matchCount} of {totalCount}
                                            </Typography>
                                        )}
                                        {draft && (
                                            <IconButton
                                                size="small"
                                                aria-label="Clear watchlist search"
                                                onClick={() => {
                                                    clear();
                                                    inputRef.current?.focus();
                                                }}
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </InputAdornment>
                                ),
                            }}
                            sx={addDialogFieldSx}
                        />
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </>
    );
}

WatchlistQuickFilter.propTypes = {
    query: PropTypes.string.isRequired,
    onQueryChange: PropTypes.func.isRequired,
    matchCount: PropTypes.number.isRequired,
    totalCount: PropTypes.number.isRequired,
    disabled: PropTypes.bool,
};
